import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ForumPost, ForumRoom, ForumTopic } from '../types';
import { IconTrash, IconPencil, IconCornerDownRight, IconArrowLeft } from '@tabler/icons-react';
import {
  listPosts,
  listRooms,
  listTopics,
  deletePost,
  createPost,
  updatePost,
} from '../services/forumService';
import { useAuth } from '../contexts/AuthContext';
import PostModal from '../components/PostModal';

export default function ForumPostsPage() {
  const { topicId: scopedTopicId } = useParams<{ topicId?: string }>();
  const isScoped = Boolean(scopedTopicId);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [rooms, setRooms] = useState<ForumRoom[]>([]);
  const [scopedTopic, setScopedTopic] = useState<ForumTopic | null>(null);
  const [loading, setLoading] = useState(true);
  const [roomFilter, setRoomFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [modalPost, setModalPost] = useState<ForumPost | null>(null);
  const [modalParentPost, setModalParentPost] = useState<ForumPost | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedTopicId]);

  useEffect(() => {
    if (!loading) {
      loadPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomFilter, searchQuery]);

  const loadData = async () => {
    try {
      setLoading(true);
      const roomsResult = await listRooms({ includeInactive: false });
      setRooms(roomsResult.rooms);

      if (isScoped && scopedTopicId) {
        const topicsResult = await listTopics({ roomFilter: undefined, searchQuery: undefined });
        const found = topicsResult.topics.find(t => t.id === scopedTopicId) ?? null;
        setScopedTopic(found);
      }

      await loadPosts();
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    try {
      const result = await listPosts({
        topicFilter: scopedTopicId ?? undefined,
        roomFilter: scopedTopicId ? undefined : roomFilter || undefined,
        searchQuery: scopedTopicId ? undefined : searchQuery || undefined,
      });
      setPosts(result.posts);
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  const handleDelete = async (post: ForumPost) => {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }
    try {
      await deletePost(post.id);
      await loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  const handleOpenReply = (parentPost: ForumPost) => {
    setModalPost(null);
    setModalParentPost(parentPost);
    setShowModal(true);
  };

  const handleOpenEdit = (post: ForumPost) => {
    setModalPost(post);
    setModalParentPost(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalPost(null);
    setModalParentPost(null);
  };

  const handleSave = async (data: { content: string }) => {
    const authorName = user?.displayName ?? user?.email?.split('@')[0] ?? 'Admin';

    if (modalPost) {
      // Edit mode
      await updatePost(modalPost.id, data.content);
    } else if (modalParentPost) {
      // Reply mode — parentPost carries topicId and roomId
      await createPost({
        topicId: modalParentPost.topicId,
        roomId: modalParentPost.roomId,
        content: data.content,
        parentPostId: modalParentPost.id,
        authorId: user!.uid,
        authorName,
      });
    }
    handleCloseModal();
    await loadPosts();
  };

  const formatDate = (value: unknown) => {
    // Firestore may return a Timestamp object (e.g. from serverTimestamp()) or an ISO string
    const date =
      value && typeof value === 'object' && 'toDate' in value
        ? (value as { toDate: () => Date }).toDate()
        : new Date(value as string);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          {isScoped ? (
            <>
              <div className="page-pretitle d-flex align-items-center gap-2">
                <button
                  className="btn btn-link p-0 text-muted"
                  onClick={() =>
                    scopedTopic
                      ? navigate(`/forum-rooms/${scopedTopic.roomId}/topics`)
                      : navigate('/forum-topics')
                  }
                >
                  <IconArrowLeft size={16} className="me-1" />
                  {scopedTopic?.roomName ?? 'Topics'}
                </button>
                <span>/</span>
                <span>{scopedTopic?.title ?? scopedTopicId}</span>
              </div>
              <h2 className="page-title">Posts</h2>
            </>
          ) : (
            <>
              <div className="page-pretitle">Forum</div>
              <h2 className="page-title">Posts</h2>
            </>
          )}
        </div>
      </div>

      {/* Filters — hidden in scoped mode */}
      {!isScoped && (
        <div className="row g-2 mt-3">
          <div className="col-md-4">
            <select
              className="form-select"
              value={roomFilter}
              onChange={e => setRoomFilter(e.target.value)}
            >
              <option value="">All Rooms</option>
              {rooms.map(room => (
                <option key={room.id} value={room.id}>
                  {room.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-8">
            <input
              type="text"
              className="form-control"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Posts List */}
      <div className="card mt-3">
        <div className="list-group list-group-flush">
          {posts.length === 0 ? (
            <div className="empty py-4">
              <p className="empty-title">No posts found</p>
              <p className="empty-subtitle text-muted">Try adjusting your filters</p>
            </div>
          ) : (
            posts.map(post => (
              <div key={post.id} className={`list-group-item${post.parentPostId ? ' ps-5' : ''}`}>
                <div className="row align-items-center">
                  <div className="col-auto">
                    {post.authorAvatar ? (
                      <img
                        src={post.authorAvatar}
                        alt={post.authorName}
                        className="avatar avatar-md"
                      />
                    ) : (
                      <span className="avatar avatar-md">{post.authorName[0]}</span>
                    )}
                  </div>
                  <div className="col">
                    <div className="mb-1">
                      <strong>{post.authorName}</strong>
                      <span className="text-muted ms-2">{formatDate(post.createdAt)}</span>
                      {post.isEdited && (
                        <span className="badge bg-secondary text-white ms-2">Edited</span>
                      )}
                      {post.parentPostId && (
                        <span className="badge bg-info text-white ms-2">Reply</span>
                      )}
                    </div>
                    <div className="text-muted mb-2">{truncateText(post.content, 200)}</div>
                    <div className="d-flex gap-2 text-muted small">
                      <span>👍 {post.likeCount}</span>
                      <span>👎 {post.dislikeCount}</span>
                      <span>🔗 {post.shareCount}</span>
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="btn-list flex-nowrap">
                      <button
                        onClick={() => handleOpenReply(post)}
                        className="btn btn-icon"
                        title="Reply"
                      >
                        <IconCornerDownRight className="icon" />
                      </button>
                      <button
                        onClick={() => handleOpenEdit(post)}
                        className="btn btn-icon"
                        title="Edit"
                      >
                        <IconPencil className="icon" />
                      </button>
                      <button
                        onClick={() => handleDelete(post)}
                        className="btn btn-icon text-danger"
                        title="Delete"
                      >
                        <IconTrash className="icon" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showModal && (
        <PostModal
          post={modalPost}
          topicTitle={
            modalPost
              ? (scopedTopic?.title ?? '')
              : modalParentPost
                ? (scopedTopic?.title ?? '')
                : ''
          }
          parentAuthorName={modalParentPost?.authorName ?? ''}
          onSave={handleSave}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
